import { useEffect, useState } from "react";
import { openHelpZettelStream } from "@/talk/talkStream";
import { deleteLocalAudio, loadLocalAudio } from "@/talk/useTalkAudioStore";
import {
  enqueueOffline,
  loadOfflineQueue,
  type TalkOfflineItem,
} from "@/talk/talkOffline";
import { isOfflineZettelId, mergeOfflineZettel } from "@/talk/offlineZettel";
import { flushOfflineQueue, isTalkRetryable } from "@/talk/talkFlush";
import { ApiErrorResponse } from "@/services/api/client";
import { TalkConfirm } from "@/talk/TalkConfirm";
import { TalkConfirmCard } from "@/talk/TalkConfirmCard";
import { WaitingCard } from "@/talk/WaitingCard";
import { ListZettelCard } from "@/talk/ListZettelCard";
import {
  revokeHelpAudioCache,
  stopTalkAudio,
  stopTalkAudioIfSrc,
} from "@/talk/helpAudio";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTalkT } from "@/talk/useTalkT";
import { helpApi } from "@/services/api/help";
import { useAuthStore } from "@/store/authStore";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { HelpPair } from "@/components/help/HelpPair";
import { ROLE_LABELS, type Role } from "@/types/role";
import { formatTime } from "@/utils/chatHelpers";
import { parseISO } from "date-fns";
import "@/talk/talk-welt.css";

function formatWait(iso: string): string {
  const min = Math.max(
    0,
    Math.round((Date.now() - parseISO(iso).getTime()) / 60000)
  );
  if (min < 60) return `${min} Min`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} Std`;
  return `${Math.round(h / 24)} Tage`;
}

function invalidateHelp(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["help-me"] });
  qc.invalidateQueries({ queryKey: ["help-my-zettel"] });
  qc.invalidateQueries({ queryKey: ["help-pairs"] });
  qc.invalidateQueries({ queryKey: ["help-inbox"] });
  qc.invalidateQueries({ queryKey: ["help-unread-count"] });
  qc.invalidateQueries({ queryKey: ["help-zettel"] });
}

function UserHelp() {
  const t = useTalkT();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [body, setBody] = useState("");

  const [localUrls, setLocalUrls] = useState<Record<string, string>>({});
  const [queue, setQueue] = useState<TalkOfflineItem[]>([]);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const {
    data: mine,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["help-me"],
    queryFn: () => helpApi.getMine(),
  });
  const { data: list } = useQuery({
    queryKey: ["help-my-zettel"],
    queryFn: () => helpApi.getMyZettel(),
    enabled: Boolean(mine?.conversation_id),
    refetchInterval: () => (document.hidden ? false : 8000),
  });

  useEffect(() => {
    if (!mine?.conversation_id) return;
    void helpApi
      .markAsRead(mine.conversation_id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["help-unread-count"] });
      })
      .catch(() => {});
  }, [mine?.conversation_id, queryClient]);

  const convId = mine?.conversation_id;
  const user = useAuthStore((s) => s.user);
  const zettel = mergeOfflineZettel(
    list?.zettel ?? [],
    queue,
    convId || "",
    user?.role || "teacher"
  );
  const selected =
    zettel.find((z) => z.id === selectedId && !isOfflineZettelId(z.id)) ??
    zettel.find((z) => !isOfflineZettelId(z.id)) ??
    null;

  useEffect(() => {
    void loadOfflineQueue().then(setQueue);
  }, []);

  useEffect(() => {
    if (!convId) return;
    const flush = async () => {
      const result = await flushOfflineQueue(convId, false);
      void loadOfflineQueue().then(setQueue);
      if (result.changed) invalidateHelp(queryClient);
      if (result.blocked) invalidateHelp(queryClient);
    };
    const onOnline = () => {
      void flush();
    };
    window.addEventListener("online", onOnline);
    void flush();
    return () => window.removeEventListener("online", onOnline);
  }, [convId, queryClient]);

  const { data: detail } = useQuery({
    queryKey: ["help-zettel", convId, selected?.id],
    queryFn: () => helpApi.getZettel(convId!, selected!.id),
    enabled: Boolean(
      convId && selected?.id && !selected.id.startsWith("offline:")
    ),
    refetchInterval: () => (document.hidden ? false : 8000),
  });
  const detailStatus = detail?.zettel.status ?? detail?.pairs[0]?.status;

  useEffect(() => {
    if (!convId || !selected?.id || selected.id.startsWith("offline:")) return;
    return openHelpZettelStream(convId, selected.id, () => {
      invalidateHelp(queryClient);
    });
  }, [convId, selected?.id, queryClient]);

  useEffect(() => {
    if (!convId || !selected?.id || !selected.status || !detailStatus) return;
    if (selected.status === detailStatus) return;
    void queryClient.invalidateQueries({
      queryKey: ["help-zettel", convId, selected.id],
    });
  }, [convId, selected?.id, selected?.status, detailStatus, queryClient]);

  const oranges = zettel.filter(
    (z) => z.status === "rueckfrage" || z.status === "bitte_bestaetigen"
  );

  useEffect(() => {
    const ids = (detail?.pairs ?? []).map((p) => p.opener.id);
    let live = true;
    const created: string[] = [];
    void Promise.all(
      ids.map(async (id) => [id, await loadLocalAudio(id)] as const)
    ).then((rows) => {
      if (!live) {
        rows.forEach(([, url]) => {
          if (url) {
            stopTalkAudioIfSrc(url);
            URL.revokeObjectURL(url);
          }
        });
        return;
      }
      const next: Record<string, string> = {};
      for (const [id, url] of rows) {
        if (url) {
          next[id] = url;
          created.push(url);
        }
      }
      setLocalUrls(next);
    });
    return () => {
      live = false;
      created.forEach((u) => {
        stopTalkAudioIfSrc(u);
        URL.revokeObjectURL(u);
      });
    };
  }, [detail?.pairs]);

  const [cardBusy, setCardBusy] = useState(false);
  const sendMut = useMutation({
    mutationFn: async (vars: { body: string; clientId: string }) => {
      if (!convId) return;
      if (
        oranges.length > 0 &&
        selected?.status !== "rueckfrage" &&
        selected?.status !== "bitte_bestaetigen"
      ) {
        setSelectedId(oranges[0]?.id ?? null);
        throw new Error("rueckfrage_pick");
      }
      const attach =
        selected?.status === "rueckfrage" ||
        selected?.status === "bitte_bestaetigen"
          ? selected.id
          : undefined;
      return helpApi.createZettel(convId, vars.body, vars.clientId, attach);
    },
    onSuccess: (res) => {
      setBody("");
      if (res?.zettel) setSelectedId(res.zettel.id);
      invalidateHelp(queryClient);
    },
    onError: async (err, vars) => {
      if (err instanceof Error && err.message === "rueckfrage_pick") return;
      if (
        err instanceof ApiErrorResponse &&
        (err.detail === "rueckfrage_pick" || err.statusCode === 409)
      ) {
        setSelectedId(oranges[0]?.id ?? null);
        return;
      }
      if (!convId || !isTalkRetryable(err)) return;
      await enqueueOffline({
        id: vars.clientId,
        conversationId: convId,
        clientId: vars.clientId,
        selectedZettelId:
          selected?.status === "rueckfrage" ||
          selected?.status === "bitte_bestaetigen"
            ? selected.id
            : undefined,
        text: vars.body,
        createdAt: new Date().toISOString(),
        preview: vars.body.slice(0, 120),
      });
      void loadOfflineQueue().then(setQueue);
    },
  });

  if (isLoading)
    return <p className="p-6 text-sm text-gray-400">{t("help_loading")}</p>;
  if (isError || !mine)
    return <p className="p-6 text-sm text-red-500">{t("help_load_error")}</p>;
  const detailZettel = detail?.zettel;
  const cardZettel =
    detailZettel?.id === selected?.id ? detailZettel : selected;

  return (
    <div className="flex flex-1 min-h-0 relative">
      <div className="w-72 border-r border-gray-200 overflow-auto bg-white">
        {zettel.length === 0 && (
          <p className="p-4 text-sm text-gray-400">{t("talk_empty")}</p>
        )}
        {zettel.map((z) =>
          isOfflineZettelId(z.id) ? (
            <div key={z.id} className="border-b border-gray-100">
              <WaitingCard z={z} light />
            </div>
          ) : (
            <div
              key={z.id}
              className={`border-b border-gray-100 ${selected?.id === z.id ? "bg-indigo-50" : ""}`}
            >
              <ListZettelCard
                z={z}
                light
                onOpen={() => setSelectedId(z.id)}
                onDelete={() => setConfirmDel(z.id)}
              />
            </div>
          )
        )}
      </div>
      <div className="flex-1 flex flex-col min-h-0 bg-gray-50 relative">
        <div className="flex-1 overflow-auto p-3">
          {detail?.pairs.map((p) => (
            <HelpPair
              key={p.opener.id}
              pair={p}
              status={cardZettel?.status ?? selected?.status}
              conversationId={convId!}
              light
              localPlayUrl={localUrls[p.opener.id]}
            />
          ))}
        </div>
        {(cardZettel?.status === "bitte_bestaetigen" ||
          cardZettel?.confirm_card?.ask) &&
        cardZettel.confirm_card &&
        convId ? (
          <TalkConfirmCard
            card={cardZettel.confirm_card}
            light
            busy={cardBusy}
            onSend={() => {
              if (cardBusy) return;
              setCardBusy(true);
              void helpApi
                .confirmZettel(convId, cardZettel.id)
                .then(() => invalidateHelp(queryClient))
                .finally(() => setCardBusy(false));
            }}
            onDiscard={() => {
              if (cardBusy) return;
              setCardBusy(true);
              void helpApi
                .discardZettel(convId, cardZettel.id)
                .then(() => invalidateHelp(queryClient))
                .finally(() => setCardBusy(false));
            }}
            onPick={(text) => {
              if (cardBusy) return;
              setCardBusy(true);
              sendMut.mutate(
                { body: text, clientId: crypto.randomUUID() },
                { onSettled: () => setCardBusy(false) }
              );
            }}
          />
        ) : null}
        {confirmDel && convId && (
          <TalkConfirm
            light
            text={t("talk_delete_confirm")}
            onCancel={() => setConfirmDel(null)}
            onConfirm={() => {
              const id = confirmDel;
              const row = zettel.find((z) => z.id === id);
              void helpApi.deactivateZettel(convId, id).then(async () => {
                if (row?.opener_id) await deleteLocalAudio(row.opener_id);
                if (selectedId === id) setSelectedId(null);
                setConfirmDel(null);
                invalidateHelp(queryClient);
              });
            }}
          />
        )}
        <form
          className="border-t border-gray-200 bg-white p-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const text = body.trim();
            if (!text) return;
            if (
              oranges.length > 0 &&
              selected?.status !== "rueckfrage" &&
              selected?.status !== "bitte_bestaetigen"
            ) {
              setSelectedId(oranges[0]?.id ?? null);
              return;
            }
            sendMut.mutate({ body: text, clientId: crypto.randomUUID() });
          }}
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={1}
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder={t("talk_keyboard_placeholder")}
          />
          <button
            type="submit"
            className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium"
          >
            {t("talk_send")}
          </button>
        </form>
      </div>
    </div>
  );
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const INBOX_PAGE = 50;

function UaThread({ conversationId }: { conversationId: string }) {
  const t = useTalkT();
  const queryClient = useQueryClient();
  const [dockId, setDockId] = useState<string | null>(null);
  const [body, setBody] = useState("");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["help-pairs", conversationId],
      queryFn: ({ pageParam }) =>
        helpApi.getPairs(conversationId, 40, pageParam?.at, pageParam?.id),
      initialPageParam: undefined as { at: string; id: string } | undefined,
      getNextPageParam: (last) => {
        const oldest = last.zettel[0]?.zettel;
        return last.has_more && oldest
          ? { at: oldest.last_activity_at, id: oldest.id }
          : undefined;
      },
      refetchInterval: () => (document.hidden ? false : 10000),
    });
  const blocks = (data?.pages ?? [])
    .slice()
    .reverse()
    .flatMap((p) => p.zettel);

  const replyMut = useMutation({
    mutationFn: () =>
      helpApi.supportReply(conversationId, dockId!, body.trim()),
    onSuccess: () => {
      setBody("");
      setDockId(null);
      invalidateHelp(queryClient);
    },
  });
  const doneMut = useMutation({
    mutationFn: (zid: string) => helpApi.markZettelDone(conversationId, zid),
    onSuccess: () => invalidateHelp(queryClient),
  });

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-auto p-3 bg-gray-50">
        {hasNextPage && (
          <button
            type="button"
            className="mb-3 text-xs font-medium text-indigo-600"
            disabled={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {isFetchingNextPage ? t("help_loading") : t("help_load_older")}
          </button>
        )}
        {blocks.map((block) => (
          <div
            key={block.zettel.id}
            className={
              block.zettel.status === "deactivated" ? "opacity-50" : ""
            }
          >
            {block.pairs.map((p) => (
              <HelpPair
                key={p.opener.id}
                pair={p}
                status={block.zettel.status}
                conversationId={conversationId}
                light
                showSupportActions={
                  block.zettel.status !== "deactivated" &&
                  block.zettel.status !== "erledigt"
                }
                onDone={
                  block.zettel.status === "bitte_bestaetigen"
                    ? undefined
                    : (id) => doneMut.mutate(id)
                }
                allowServerPlay
                onSupportReply={(id) => setDockId(id)}
                composer={
                  dockId === p.zettel_id ? (
                    <form
                      className="mt-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (body.trim()) replyMut.mutate();
                      }}
                    >
                      <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={2}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder={t("help_reply_as_support")}
                      />
                      <div className="mt-2 flex gap-2">
                        <button
                          type="submit"
                          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm"
                        >
                          {t("talk_send")}
                        </button>
                        <button
                          type="button"
                          className="text-sm text-gray-500"
                          onClick={() => setDockId(null)}
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </form>
                  ) : null
                }
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function UltraadminHelp() {
  const t = useTalkT();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"open" | "done">("open");
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 300);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["help-inbox", status, qDebounced],
    queryFn: ({ pageParam }) =>
      helpApi.getInbox(status, qDebounced, INBOX_PAGE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.has_more
        ? pages.reduce((n, p) => n + p.tickets.length, 0)
        : undefined,
    refetchInterval: () => (document.hidden ? false : 10000),
  });

  const select = (id: string) => {
    setSelectedId(id);
    void helpApi
      .markAsRead(id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["help-inbox"] });
        queryClient.invalidateQueries({ queryKey: ["help-unread-count"] });
      })
      .catch(() => {});
  };

  const tickets = data?.pages.flatMap((p) => p.tickets) ?? [];
  const openCount = data?.pages[0]?.open_count ?? 0;

  return (
    <div className="flex flex-1 min-h-0">
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="h-14 bg-white border-b border-gray-200 flex items-center px-4 gap-3 shrink-0">
          <h1 className="text-sm font-semibold text-gray-900">
            {t("help_title")}
          </h1>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setStatus("open")}
              className={`px-2.5 py-1 text-[11px] rounded-full font-medium ${status === "open" ? "bg-slate-900 text-white" : "bg-gray-100 text-gray-600"}`}
            >
              {t("help_open")}
              {openCount ? ` ${openCount}` : ""}
            </button>
            <button
              type="button"
              onClick={() => setStatus("done")}
              className={`px-2.5 py-1 text-[11px] rounded-full font-medium ${status === "done" ? "bg-slate-900 text-white" : "bg-gray-100 text-gray-600"}`}
            >
              {t("help_done")}
            </button>
          </div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("help_search_name")}
            className="ml-auto w-56 h-8 px-3 text-xs rounded-lg border border-gray-200"
          />
        </div>
        <div className="flex-1 overflow-auto p-3 bg-gray-50">
          {isLoading && (
            <p className="p-4 text-sm text-gray-400">{t("help_loading")}</p>
          )}
          {isError && (
            <p className="p-4 text-sm text-red-500">{t("help_load_error")}</p>
          )}
          {!isLoading && !isError && tickets.length === 0 && (
            <p className="p-4 text-sm text-gray-400">{t("help_empty_inbox")}</p>
          )}
          {tickets.length > 0 && (
            <table className="w-full text-xs bg-white border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="text-left px-3 py-2">{t("help_person")}</th>
                  <th className="text-left px-3 py-2">{t("help_role")}</th>
                  <th className="text-left px-3 py-2">{t("help_school")}</th>
                  <th className="text-left px-3 py-2">
                    {status === "open" ? t("help_waiting") : t("help_done")}
                  </th>
                  <th className="text-left px-3 py-2">{t("help_status")}</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => {
                  const active = ticket.conversation_id === selectedId;
                  return (
                    <tr
                      key={ticket.conversation_id}
                      onClick={() => select(ticket.conversation_id)}
                      className={`border-t border-gray-100 cursor-pointer ${active ? "bg-indigo-50/50" : "hover:bg-gray-50"}`}
                    >
                      <td className="px-3 py-2.5 font-medium text-gray-900">
                        {ticket.user_name}
                      </td>
                      <td className="px-3 py-2.5">
                        {ROLE_LABELS[ticket.role as Role] ?? ticket.role}
                      </td>
                      <td className="px-3 py-2.5">
                        {ticket.school_name || "—"}
                      </td>
                      <td
                        className={`px-3 py-2.5 ${status === "open" ? "text-amber-700 font-medium" : ""}`}
                      >
                        {status === "open"
                          ? ticket.last_message
                            ? formatWait(ticket.last_message.created_at)
                            : "—"
                          : ticket.done_at
                            ? formatTime(ticket.done_at)
                            : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {ticket.is_done ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                            {t("help_done")}
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            {t("help_open")}
                          </span>
                        )}
                        {ticket.unread_count > 0 && (
                          <span className="ml-1 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500">
                            {ticket.unread_count > 99
                              ? "99+"
                              : ticket.unread_count}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {hasNextPage && (
            <button
              type="button"
              className="mt-3 text-xs font-medium text-indigo-600"
              disabled={isFetchingNextPage}
              onClick={() => {
                void fetchNextPage();
              }}
            >
              {isFetchingNextPage ? t("help_loading") : t("help_load_more")}
            </button>
          )}
        </div>
      </div>
      <div className="w-[340px] border-l border-gray-200 bg-white flex flex-col min-h-0 shrink-0">
        {selectedId ? (
          <UaThread conversationId={selectedId} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
            {t("help_pick_entry")}
          </div>
        )}
      </div>
    </div>
  );
}

export function HelpPage() {
  const role = useAuthStore((s) => s.user?.role);
  useEffect(
    () => () => {
      stopTalkAudio();
      revokeHelpAudioCache();
    },
    []
  );
  return (
    <ErrorBoundary>
      <div className="flex flex-col h-[calc(100vh-3.5rem)] min-w-0 overflow-hidden">
        {role === "ultraadmin" ? <UltraadminHelp /> : <UserHelp />}
      </div>
    </ErrorBoundary>
  );
}
